// SPDX-FileCopyrightText: 2026 Ben Richardson
// SPDX-License-Identifier: AGPL-3.0-or-later
//
// "What for" -- what the Order of Australia has actually been awarded FOR,
// read out of 48,781 citations.
//
// The second artefact is the point of the view. A raw domain x level matrix
// would only ever say "OAM is the biggest column", because OAM is 64% of the
// Order; so every cell is drawn as a standardised residual against the count
// you would expect if level and field were independent. That is what exposes
// the real structure: law, science and education are pulled towards the top of
// the Order, while sport, welfare, emergency services and general community
// service are pulled towards the Medal.

import type { Core, View } from '../types';
import { domainColour, divergingColour } from '../palette';
import { num, dec, pct, escapeHtml, tipAttr } from '../format';
import { gloss } from '../glossary';

const LEVEL_CODES = ['AC', 'AO', 'AM', 'OAM'];

/** Colour cap for the diverging scale, in standardised-residual units. */
const CAP = 12;

interface Cell {
  code: string;
  levelLabel: string;
  obs: number;
  exp: number;
  z: number;
}

interface Row {
  id: string;
  label: string;
  total: number;
  topShare: number;
  cells: Cell[];
  isOther: boolean;
}

interface SparkPoint {
  year: number;
  n: number;
  total: number;
  share: number;
}

/** Ink that stays legible on a given cell fill. */
function ink(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  if (!Number.isFinite(r) || !Number.isFinite(g) || !Number.isFinite(b)) return '#1b2330';
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.58 ? '#1b2330' : '#ffffff';
}

function signed(z: number): string {
  if (!Number.isFinite(z)) return '—';
  return `${z >= 0 ? '+' : '−'}${dec(Math.abs(z), 1)}`;
}

function clip(s: string, chars: number): string {
  if (chars < 2) return '';
  return s.length <= chars ? s : `${s.slice(0, chars - 1).replace(/[\s,&]+$/, '')}…`;
}

function ticks(max: number, want: number): number[] {
  if (!(max > 0) || !(want > 0)) return [0];
  const raw = max / want;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const norm = raw / mag;
  const step = (norm > 5 ? 10 : norm > 2 ? 5 : norm > 1 ? 2 : 1) * mag;
  if (!(step > 0)) return [0];
  const out: number[] = [];
  for (let v = 0; v <= max + step * 0.001 && out.length < 12; v += step) out.push(v);
  return out;
}

function tickLabel(v: number): string {
  return v >= 1000 ? `${Math.round(v / 100) / 10}k` : String(Math.round(v));
}

/** Rows of the matrix: observed, expected and residual for every domain x level. */
function buildRows(core: Core, labelOf: (id: string) => string): Row[] {
  const byLevel = core.domainByLevel ?? {};
  const levels = LEVEL_CODES.map((code) => {
    const found = core.oaLevels.find((l) => l.code === code);
    return { code, label: found?.label ?? code };
  });

  const ids = core.domainTotals.map((d) => d.id).filter((id) => byLevel[id]);
  const rowTotal = new Map<string, number>();
  const colTotal = new Map<string, number>();
  let grand = 0;
  for (const id of ids) {
    let t = 0;
    for (const l of levels) {
      const v = byLevel[id]?.[l.code] ?? 0;
      t += v;
      colTotal.set(l.code, (colTotal.get(l.code) ?? 0) + v);
    }
    rowTotal.set(id, t);
    grand += t;
  }

  const rows: Row[] = ids.map((id) => {
    const total = rowTotal.get(id) ?? 0;
    const cells: Cell[] = levels.map((l) => {
      const obs = byLevel[id]?.[l.code] ?? 0;
      const exp = grand > 0 ? (total * (colTotal.get(l.code) ?? 0)) / grand : 0;
      const z = exp > 0 ? (obs - exp) / Math.sqrt(exp) : 0;
      return { code: l.code, levelLabel: l.label, obs, exp, z };
    });
    const top = cells
      .filter((c) => c.code === 'AC' || c.code === 'AO')
      .reduce((s, c) => s + c.obs, 0);
    return {
      id,
      label: labelOf(id),
      total,
      topShare: total > 0 ? top / total : 0,
      cells,
      isOther: id === 'other',
    };
  });

  // Seriation: order by the share of a field's citations that sit at the top
  // two levels of the Order, so similar profiles end up as neighbours. The
  // residual "other" bucket is not a field of service and is parked last.
  rows.sort((a, b) => b.topShare - a.topShare);
  return [...rows.filter((r) => !r.isOther), ...rows.filter((r) => r.isOther)];
}

export const whatForView: View = (root, ctx) => {
  const core = ctx.core;

  const labels = new Map(core.domains.map((d) => [d.id, d.label]));
  const labelOf = (id: string): string =>
    labels.get(id) ?? (id === 'other' ? 'Other — unclassified' : id);

  const rows = buildRows(core, labelOf);
  const rowById = new Map(rows.map((r) => [r.id, r]));

  const colTotals = new Map<string, number>();
  for (const r of rows) for (const c of r.cells) colTotals.set(c.code, (colTotals.get(c.code) ?? 0) + c.obs);
  const grand = rows.reduce((s, r) => s + r.total, 0);

  // Ranked bars use the published domain totals, which include the handful of
  // Knight/Dame citations the four-level matrix leaves out.
  const barRows = core.domainTotals.map((d) => ({
    id: d.id,
    label: labelOf(d.id),
    n: d.n,
  }));
  const barGrand = barRows.reduce((s, d) => s + d.n, 0);
  const barMax = barRows.reduce((m, d) => Math.max(m, d.n), 0);

  // ---- time series -------------------------------------------------------
  const FROM = 1980;
  const years = core.domainByYear
    .filter((y) => y.year >= FROM)
    .slice()
    .sort((a, b) => a.year - b.year);
  const yearTotals = years.map((y) => Object.values(y.counts).reduce((a, b) => a + b, 0));
  const lastYear = years.length ? years[years.length - 1].year : FROM;
  const firstYear = core.domainByYear.reduce((m, y) => Math.min(m, y.year), FROM);

  const series = new Map<string, SparkPoint[]>();
  for (const r of barRows) {
    const pts: SparkPoint[] = years.map((y, i) => {
      const n = y.counts[r.id] ?? 0;
      const total = yearTotals[i] ?? 0;
      return { year: y.year, n, total, share: total > 0 ? n / total : 0 };
    });
    series.set(r.id, pts);
  }
  const windowShare = (id: string, a: number, b: number): number => {
    let n = 0;
    let t = 0;
    for (const p of series.get(id) ?? []) {
      if (p.year < a || p.year > b) continue;
      n += p.n;
      t += p.total;
    }
    return t > 0 ? n / t : NaN;
  };

  const otherTotal = barRows.find((r) => r.id === 'other')?.n ?? 0;
  const topField = rows.find((r) => !r.isOther);
  const bottomField = [...rows].reverse().find((r) => !r.isOther);

  // ---- scaffolding -------------------------------------------------------
  const wrap = document.createElement('div');
  root.appendChild(wrap);

  const style = document.createElement('style');
  style.textContent = `
    .wf-controls{display:flex;flex-wrap:wrap;gap:var(--space-sm);align-items:center;margin-bottom:var(--space-lg)}
    .wf-controls label{font-size:var(--font-size-sm);color:var(--text-secondary)}
    .wf-controls select{max-width:100%}
    .wf-readout{border:1px solid var(--border-subtle);background:var(--bg-elevated);
      border-radius:var(--radius-sm);padding:var(--space-md);font-size:var(--font-size-sm);
      color:var(--text-secondary);margin-top:var(--space-md);max-width:82ch}
    .wf-readout strong{color:var(--text-primary)}
    .wf-scroll{overflow-x:auto;-webkit-overflow-scrolling:touch}
    .wf-matrix{display:block;min-width:592px;height:auto}
    .wf-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(min(190px,100%),1fr));
      gap:var(--space-md);margin-top:var(--space-md)}
    .wf-card{font:inherit;text-align:left;background:var(--bg-surface);border:1px solid var(--border-subtle);
      border-radius:var(--radius-sm);padding:var(--space-sm);cursor:pointer;min-width:0;
      display:flex;flex-direction:column;gap:2px}
    .wf-card:hover{background:var(--bg-hover)}
    .wf-card[aria-pressed='true']{border-color:var(--accent-primary);box-shadow:0 0 0 2px var(--accent-wash)}
    .wf-card.is-dim{opacity:.35}
    .wf-card-name{display:block;min-width:0;font-size:var(--font-size-sm);font-weight:600;
      color:var(--text-primary);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
    .wf-card-foot{display:flex;justify-content:space-between;gap:var(--space-xs);
      font-size:var(--font-size-xs);color:var(--text-tertiary);font-variant-numeric:tabular-nums}
    .wf-card-foot span:first-child{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
    .wf-card-foot span:last-child{flex-shrink:0}
    .wf-spark{display:block;width:100%;height:auto}
    .wf-key{display:flex;flex-wrap:wrap;align-items:center;gap:2px;margin-top:var(--space-sm)}
    .wf-key-cell{width:34px;height:16px;flex-shrink:0;border:1px solid rgba(0,0,0,.12)}
    .wf-key-lab{font-size:var(--font-size-xs);color:var(--text-tertiary);
      width:34px;text-align:center;flex-shrink:0;font-variant-numeric:tabular-nums}
    .wf-bar-hit,.wf-cell,.wf-rowhit{cursor:pointer}
  `;
  wrap.appendChild(style);

  const params = new URLSearchParams(root.dataset.params ?? '');
  const initial = params.get('d');
  let selected: string | null = initial && rowById.has(initial) ? initial : null;

  const head = document.createElement('div');
  head.className = 'view-head';
  head.innerHTML = `
    <h1>What Australia honours people for</h1>
    <p class="lede">Every Order of Australia award carries a ${gloss('citation', 'citation')} — a
      single published line beginning “For service to…”. Reading all
      ${num(core.totals.citations)} of them sorts the Order into fields of service, and then a second
      question opens up: not which field gets the most awards, but which fields are carried to the
      <em>top</em> of the Order and which stop at the Medal.</p>
    <div class="note">
      <p><strong>These groupings are ours, not the register’s.</strong> The honours register publishes no
      subject field. Each ${gloss('service-domain', 'service domain')} here was derived by reading the
      citation text, so the boundaries are editorial and a citation that spans two fields is filed under one.
      ${num(otherTotal)} citations (${pct(barGrand > 0 ? otherTotal / barGrand : NaN)}) could not be placed and sit in
      “other”, which is a residual bucket rather than a field of service.</p>
      <p style="margin-top:.5rem">This view covers the ${num(core.totals.citations)} citations attached to
      ${num(core.totals.oa)} ${gloss('order', 'Order of Australia')} awards —
      ${pct(core.totals.records > 0 ? core.totals.citations / core.totals.records : NaN)} of the
      ${num(core.totals.records)} records in the register. Long-service and campaign medals, including the
      ${gloss('national-medal', 'National Medal')}, carry no citation at all, so nothing here describes them.</p>
    </div>`;
  wrap.appendChild(head);

  const stats = document.createElement('div');
  stats.className = 'stats';
  stats.innerHTML = [
    [num(core.totals.citations), 'citations read and classified'],
    [String(core.domains.length), 'fields of service, plus an “other” bucket'],
    [
      topField ? pct(topField.topShare) : '—',
      topField ? `of ${escapeHtml(topField.label)} citations sit at AC or AO — the highest of any field` : '',
    ],
    [
      bottomField ? pct(bottomField.topShare) : '—',
      bottomField ? `of ${escapeHtml(bottomField.label)} citations do — the lowest` : '',
    ],
  ]
    .map(
      ([v, l]) =>
        `<div class="stat"><div class="stat-value">${v}</div><div class="stat-label">${l}</div></div>`,
    )
    .join('');
  wrap.appendChild(stats);

  // ---- selection control -------------------------------------------------
  const controls = document.createElement('div');
  controls.className = 'wf-controls';
  controls.innerHTML = `
    <label for="wf-pick">Highlight a field of service</label>
    <select id="wf-pick">
      <option value="">All fields</option>
      ${barRows
        .map((r) => `<option value="${escapeHtml(r.id)}">${escapeHtml(r.label)} (${num(r.n)})</option>`)
        .join('')}
    </select>
    <button type="button" class="pill" id="wf-clear">Clear highlight</button>`;
  wrap.appendChild(controls);
  const pick = controls.querySelector<HTMLSelectElement>('#wf-pick')!;
  const clearBtn = controls.querySelector<HTMLButtonElement>('#wf-clear')!;

  // ---- panel 1: ranked bars ---------------------------------------------
  const p1 = document.createElement('section');
  p1.className = 'panel';
  p1.innerHTML = `
    <div class="panel-head">
      <h2>Which fields the Order recognises most</h2>
      <p class="panel-sub">Every classified citation, ${firstYear}–${lastYear}, counted into one field of
        service. Bars are lengths on a shared scale starting at zero: longer means more citations. Select a bar
        to carry that field through to the two charts below.</p>
    </div>
    <div class="chart-wrap" id="wf-bars"></div>
    <div class="legend">
      ${barRows
        .slice(0, 6)
        .map(
          (r) =>
            `<span class="legend-item"><span class="swatch" style="background:${domainColour(r.id)}"></span>
              ${escapeHtml(r.label)}</span>`,
        )
        .join('')}
      <span class="legend-item">…and ${barRows.length - 6} more, each named on its own bar. A field keeps the
        same colour in every chart on this site.</span>
      <span class="legend-item">Selecting a field dims the rest rather than hiding them.</span>
    </div>
    <p class="panel-sub" style="margin-top:var(--space-sm)">“Community (general)” is large because a great many
      citations read simply “for service to the community of …”, naming a town rather than a field.</p>`;
  wrap.appendChild(p1);
  const barWrap = p1.querySelector<HTMLDivElement>('#wf-bars')!;

  function barSvg(w: number): string {
    const width = Math.max(260, Math.round(w) || 640);
    const fs = width < 430 ? 10 : 11;
    const rowH = width < 430 ? 25 : 27;
    const topPad = 24;
    const botPad = 42;
    const labelW = Math.max(92, Math.min(212, Math.round(width * 0.42)));
    const valueW = width < 430 ? 54 : 96;
    const x0 = labelW + 8;
    const plotW = Math.max(30, width - x0 - valueW - 4);
    const h = topPad + barRows.length * rowH + botPad;
    const sx = (v: number): number => (barMax > 0 ? (v / barMax) * plotW : 0);
    const chars = Math.max(3, Math.floor((labelW - 8) / (fs * 0.56)));

    const grid = ticks(barMax, width < 430 ? 2 : 4)
      .map((t) => {
        const x = x0 + sx(t);
        return `<line x1="${x.toFixed(1)}" y1="${topPad - 6}" x2="${x.toFixed(1)}" y2="${topPad + barRows.length * rowH}"
            stroke="var(--border-subtle)" stroke-width="1"></line>
          <text x="${x.toFixed(1)}" y="${topPad + barRows.length * rowH + 15}" text-anchor="middle"
            style="font-size:${fs - 1}px;fill:var(--text-tertiary)">${tickLabel(t)}</text>`;
      })
      .join('');

    const body = barRows
      .map((r, i) => {
        const y = topPad + i * rowH;
        const bw = Math.max(1, sx(r.n));
        const share = barGrand > 0 ? r.n / barGrand : NaN;
        const pts = series.get(r.id) ?? [];
        const first = pts.length ? pts[0] : null;
        const tip = [
          r.label,
          `${num(r.n)} citations — ${pct(share)} of all ${num(barGrand)} classified`,
          `Rank ${i + 1} of ${barRows.length}`,
          first ? `${first.year}–${lastYear}, Order of Australia citations only` : '',
        ]
          .filter(Boolean)
          .join('\n');
        const val =
          width < 430 ? num(r.n) : `${num(r.n)}  ·  ${pct(share)}`;
        return `<g data-domain="${escapeHtml(r.id)}">
            <text x="${labelW}" y="${(y + rowH / 2 + fs * 0.36).toFixed(1)}" text-anchor="end"
              style="font-size:${fs}px;fill:var(--text-primary)">${escapeHtml(clip(r.label, chars))}</text>
            <rect x="${x0}" y="${(y + 3).toFixed(1)}" width="${bw.toFixed(1)}" height="${(rowH - 8).toFixed(1)}"
              rx="2" fill="${domainColour(r.id)}"></rect>
            <text x="${(x0 + bw + 6).toFixed(1)}" y="${(y + rowH / 2 + fs * 0.36).toFixed(1)}"
              style="font-size:${fs - 0.5}px;fill:var(--text-secondary)">${escapeHtml(val)}</text>
            <rect class="wf-bar-hit" x="0" y="${y.toFixed(1)}" width="${width}" height="${rowH}"
              fill="rgba(0,0,0,0)" pointer-events="all" tabindex="0" role="button"
              aria-label="${escapeHtml(r.label)}: ${num(r.n)} citations, ${pct(share)} of the classified total. Select to highlight."
              data-tip="${tipAttr(tip)}" data-domain="${escapeHtml(r.id)}"></rect>
          </g>`;
      })
      .join('');

    return `<svg class="chart" viewBox="0 0 ${width} ${h}" width="${width}" height="${h}"
        role="group" aria-label="Order of Australia citations by field of service, ranked">
        <text x="0" y="12" style="font-size:${fs - 1}px;fill:var(--text-tertiary)">Field of service</text>
        <text x="${x0}" y="12" style="font-size:${fs - 1}px;fill:var(--text-tertiary)">Citations →</text>
        ${grid}
        <line x1="${x0}" y1="${topPad - 6}" x2="${x0}" y2="${topPad + barRows.length * rowH}"
          stroke="var(--border-default)" stroke-width="1"></line>
        ${body}
        <text x="${x0}" y="${h - 10}" style="font-size:${fs - 1}px;fill:var(--text-tertiary)">Number of Order of Australia citations classified into the field</text>
      </svg>`;
  }

  let drawnAt = -1;
  function drawBars(): void {
    const w = barWrap.clientWidth || 640;
    if (Math.abs(w - drawnAt) < 6) return;
    drawnAt = w;
    barWrap.innerHTML = barSvg(w);
    paintBars();
  }

  // ---- panel 2: residual matrix -----------------------------------------
  const p2 = document.createElement('section');
  p2.className = 'panel';

  const PAD = 8;
  const LABW = 164;
  const CW = 70;
  const CH = 21;
  const ROWH = 26;
  const HEAD = 50;
  const cx0 = PAD + LABW;
  const totX = cx0 + 4 * CW + 10;
  const totW = 54;
  const shX = totX + totW + 6;
  const shW = 66;
  const MW = shX + shW + PAD;
  const GAPY = 16;
  const mainRows = rows.filter((r) => !r.isOther);
  const otherRows = rows.filter((r) => r.isOther);
  const bodyH = mainRows.length * ROWH + (otherRows.length ? GAPY + otherRows.length * ROWH : 0);
  const MH = HEAD + bodyH + 30;

  function rowSvg(r: Row, y: number): string {
    const chars = Math.max(6, Math.floor((LABW - 10) / 6.1));
    const cells = r.cells
      .map((c, j) => {
        const fill = divergingColour(c.z, CAP);
        const x = cx0 + j * CW + 2;
        const tip = [
          `${r.label} × ${c.levelLabel}`,
          `Observed ${num(c.obs)} · expected ${dec(c.exp, 1)}`,
          `Standardised residual ${signed(c.z)} (${c.z >= 0 ? 'more' : 'fewer'} than expected)`,
          `Field total ${num(r.total)} citations · ${c.code} total ${num(colTotals.get(c.code) ?? 0)}`,
        ].join('\n');
        return `<rect class="wf-cell" x="${x}" y="${y + 2.5}" width="${CW - 4}" height="${CH}" rx="2"
            fill="${fill}" stroke="rgba(0,0,0,.08)" data-domain="${escapeHtml(r.id)}"
            aria-hidden="true" data-tip="${tipAttr(tip)}"></rect>
          <text x="${x + (CW - 4) / 2}" y="${y + 2.5 + CH / 2 + 3.6}" text-anchor="middle" pointer-events="none"
            style="font-size:10px;fill:${ink(fill)}">${signed(c.z)}</text>`;
      })
      .join('');
    const rowTip = [
      r.label,
      `${num(r.total)} citations across AC, AO, AM and OAM`,
      `${pct(r.topShare)} of them at the top two levels (AC or AO)`,
      r.isOther ? 'Residual bucket — not a field of service' : 'Select to highlight this field',
    ].join('\n');
    const nameStyle = r.isOther
      ? 'font-size:11px;fill:var(--text-tertiary);font-style:italic'
      : 'font-size:11px;fill:var(--text-primary)';
    return `<g data-domain="${escapeHtml(r.id)}">
        <rect class="wf-rowbg" x="${PAD}" y="${y}" width="${MW - 2 * PAD}" height="${ROWH}" rx="3"
          fill="none" stroke="var(--accent-primary)" stroke-width="2" opacity="0"></rect>
        <rect x="${PAD}" y="${y + 2.5}" width="4" height="${CH}" rx="1" fill="${domainColour(r.id)}"></rect>
        <text x="${PAD + 10}" y="${y + ROWH / 2 + 4}" style="${nameStyle}">${escapeHtml(clip(r.label, chars))}</text>
        <text x="${totX + totW}" y="${y + ROWH / 2 + 4}" text-anchor="end"
          style="font-size:10px;fill:var(--text-secondary);font-variant-numeric:tabular-nums">${num(r.total)}</text>
        <text x="${shX + shW}" y="${y + ROWH / 2 + 4}" text-anchor="end"
          style="font-size:10px;fill:var(--text-secondary);font-variant-numeric:tabular-nums">${pct(r.topShare)}</text>
        ${cells}
        <rect class="wf-rowhit" x="${PAD}" y="${y}" width="${LABW}" height="${ROWH}" fill="rgba(0,0,0,0)"
          pointer-events="all" tabindex="0" role="button" data-domain="${escapeHtml(r.id)}"
          aria-label="${escapeHtml(r.label)}: ${num(r.total)} citations, ${pct(r.topShare)} at AC or AO. Residuals ${r.cells
            .map((c) => `${c.code} ${signed(c.z)}`)
            .join(', ')}. Select to highlight."
          data-tip="${tipAttr(rowTip)}"></rect>
      </g>`;
  }

  const header = LEVEL_CODES.map((code, j) => {
    const cell = rows[0]?.cells[j];
    const x = cx0 + j * CW + (CW - 4) / 2 + 2;
    const tip = `${cell?.levelLabel ?? code}\n${num(colTotals.get(code) ?? 0)} citations at this level — ${pct(
      grand > 0 ? (colTotals.get(code) ?? 0) / grand : NaN,
    )} of the ${num(grand)} in the matrix`;
    return `<g data-tip="${tipAttr(tip)}">
        <text x="${x}" y="30" text-anchor="middle" style="font-size:12px;font-weight:600;fill:var(--text-primary)">${code}</text>
        <text x="${x}" y="42" text-anchor="middle" style="font-size:9.5px;fill:var(--text-tertiary)">n=${num(
          colTotals.get(code) ?? 0,
        )}</text>
      </g>`;
  }).join('');

  let yCursor = HEAD;
  const mainSvg = mainRows
    .map((r) => {
      const s = rowSvg(r, yCursor);
      yCursor += ROWH;
      return s;
    })
    .join('');
  const sepY = yCursor + GAPY / 2;
  const otherSvg = otherRows
    .map((r) => {
      const s = rowSvg(r, yCursor + GAPY);
      yCursor += GAPY + ROWH;
      return s;
    })
    .join('');

  const keySteps = [-CAP, -8, -5, -3, -1.5, 0, 1.5, 3, 5, 8, CAP];
  const keyHtml = `<div class="wf-key" aria-hidden="true">${keySteps
    .map(
      (z) =>
        `<span class="wf-key-cell" style="background:${divergingColour(z, CAP)}"
          data-tip="${tipAttr(
            `Standardised residual ${signed(z)}${Math.abs(z) >= CAP ? ' or beyond' : ''}\n${
              z > 0 ? 'more' : z < 0 ? 'fewer' : 'exactly as many'
            } citations than expected`,
          )}"></span>`,
    )
    .join('')}</div>
    <div class="wf-key" aria-hidden="true">${keySteps
      .map((z) => `<span class="wf-key-lab">${Math.abs(z) === CAP ? `${z < 0 ? '≤−' : '≥+'}${CAP}` : signed(z)}</span>`)
      .join('')}</div>`;

  p2.innerHTML = `
    <div class="panel-head">
      <h2>Which fields reach the top of the Order</h2>
      <p class="panel-sub">A heatmap of ${num(grand)} citations: one row per field of service, one column per
        level of the Order — ${gloss('ac', 'AC')}, ${gloss('ao', 'AO')}, ${gloss('am', 'AM')} and
        ${gloss('oam', 'OAM')}. Cells are <strong>not</strong> counts. Because OAM is
        ${pct(grand > 0 ? (colTotals.get('OAM') ?? 0) / grand : NaN)} of the Order, a count map would just be dark
        down one column. Each cell instead shows how far its count sits from the number you would expect if
        level and field were unrelated — expected = field total × level total ÷ grand total, and the
        cell is (observed − expected) ÷ √expected, a standardised residual. Roughly, anything past
        ±2 is more than random wobble.</p>
    </div>
    <div class="wf-scroll">
      <svg class="chart wf-matrix" viewBox="0 0 ${MW} ${MH}" width="${MW}" height="${MH}"
        role="group" aria-label="Field of service by level of the Order of Australia, shaded by standardised residual">
        <text x="${PAD}" y="16" style="font-size:9.5px;fill:var(--text-tertiary)">Field of service ↓ (ordered by share at AC or AO)</text>
        <text x="${cx0}" y="16" style="font-size:9.5px;fill:var(--text-tertiary)">Level of the Order of Australia →</text>
        <text x="${totX + totW}" y="42" text-anchor="end" style="font-size:9.5px;fill:var(--text-tertiary)">Citations</text>
        <text x="${shX + shW}" y="42" text-anchor="end" style="font-size:9.5px;fill:var(--text-tertiary)">% AC/AO</text>
        ${header}
        ${mainSvg}
        ${otherRows.length ? `<line x1="${PAD}" y1="${sepY}" x2="${MW - PAD}" y2="${sepY}" stroke="var(--border-default)" stroke-dasharray="3 3"></line>` : ''}
        ${otherSvg}
        <text x="${PAD}" y="${MH - 10}" style="font-size:9.5px;fill:var(--text-tertiary)">Blue = fewer awards at that level than expected · red = more · pale = about as expected</text>
      </svg>
    </div>
    ${keyHtml}
    <div class="legend">
      <span class="legend-item"><span class="swatch" style="background:${divergingColour(-CAP, CAP)}"></span>
        Fewer than expected</span>
      <span class="legend-item"><span class="swatch" style="background:${divergingColour(0, CAP)}"></span>
        About as expected</span>
      <span class="legend-item"><span class="swatch" style="background:${divergingColour(CAP, CAP)}"></span>
        More than expected</span>
      <span class="legend-item">Colour saturates at ±${CAP}; the number printed in each cell is the true residual.</span>
    </div>
    <div id="wf-readout" class="wf-readout"></div>
    <p class="panel-sub" style="margin-top:var(--space-md)">Read across a row, not down a column: the comparison is
      <strong>within the Order of Australia only</strong>, and it describes how the Order has distributed its levels
      across fields — not what any field’s work is worth. The
      ${otherRows.length ? 'greyed row below the dashed line is the residual “other” bucket; it is kept in the arithmetic so the column totals stay whole, but it is not a field and its cells mean nothing on their own. ' : ''}
      A handful of Knight and Dame citations sit outside these four columns and are excluded, which is why the matrix
      holds ${num(grand)} citations against ${num(core.totals.citations)} classified.</p>`;
  wrap.appendChild(p2);
  const matrixSvgEl = p2.querySelector<SVGSVGElement>('.wf-matrix')!;
  const readout = p2.querySelector<HTMLDivElement>('#wf-readout')!;

  // ---- panel 3: share over time -----------------------------------------
  const SW = 200;
  const SH = 42;

  function sparkSvg(id: string, label: string): string {
    const pts = series.get(id) ?? [];
    const n = pts.length;
    const maxShare = pts.reduce((m, p) => Math.max(m, p.share), 0);
    if (n === 0) return `<svg class="wf-spark" viewBox="0 0 ${SW} ${SH}" role="group" aria-label="No data"></svg>`;
    const xAt = (i: number): number => (n > 1 ? 3 + (i / (n - 1)) * (SW - 6) : SW / 2);
    const yAt = (s: number): number => (maxShare > 0 ? SH - 4 - (s / maxShare) * (SH - 10) : SH - 4);
    const line = pts.map((p, i) => `${xAt(i).toFixed(1)},${yAt(p.share).toFixed(1)}`).join(' ');
    const bw = SW / n;
    const hits = pts
      .map((p, i) => {
        const tip = `${p.year} — ${label}\n${pct(p.share)} of that year’s citations (${num(p.n)} of ${num(
          p.total,
        )})${p.year === lastYear ? '\n' + lastYear + ' is a part-year' : ''}`;
        return `<rect x="${(i * bw).toFixed(2)}" y="0" width="${bw.toFixed(2)}" height="${SH}"
          fill="rgba(0,0,0,0)" pointer-events="all" data-tip="${tipAttr(tip)}"></rect>`;
      })
      .join('');
    const last = pts[n - 1];
    return `<svg class="wf-spark" viewBox="0 0 ${SW} ${SH}" preserveAspectRatio="none"
        role="group" aria-label="${escapeHtml(label)}: share of citations each year from ${pts[0].year} to ${lastYear}, peak ${pct(maxShare)}">
        <line x1="3" y1="${SH - 4}" x2="${SW - 3}" y2="${SH - 4}" stroke="var(--border-subtle)"></line>
        <polyline points="${line}" fill="none" stroke="${domainColour(id)}" stroke-width="1.6"
          stroke-linejoin="round" vector-effect="non-scaling-stroke"></polyline>
        <circle cx="${xAt(n - 1).toFixed(1)}" cy="${yAt(last.share).toFixed(1)}" r="2" fill="${domainColour(id)}"></circle>
        ${hits}
      </svg>`;
  }

  const p3 = document.createElement('section');
  p3.className = 'panel';
  p3.innerHTML = `
    <div class="panel-head">
      <h2>How each field’s share has moved, ${FROM}–${lastYear}</h2>
      <p class="panel-sub">One line per field. The vertical axis is that field’s share of <em>that year’s</em>
        citations, so growth in the Order overall cannot make a line rise on its own. Each line is scaled to its
        own peak, so heights are <strong>not</strong> comparable between cards — shape is. Hover any year for the
        exact count; select a card to carry the field through to the charts above. Years before ${FROM} are left out
        because the Order was still filling out and single citations swung the share; ${lastYear} covers only the
        lists gazetted so far.</p>
    </div>
    <div class="wf-grid" id="wf-sparks">
      ${barRows
        .map((r) => {
          const early = windowShare(r.id, FROM, FROM + 9);
          const late = windowShare(r.id, 2020, lastYear);
          return `<button type="button" class="wf-card" data-domain="${escapeHtml(r.id)}" aria-pressed="false">
              <span class="wf-card-name" data-tip="${tipAttr(
                `${r.label}\n${num(r.n)} citations in total\n${FROM}s share ${pct(early)} → 2020s share ${pct(late)}`,
              )}">${escapeHtml(r.label)}</span>
              ${sparkSvg(r.id, r.label)}
              <span class="wf-card-foot"><span>${FROM}s ${pct(early)}</span><span>2020s ${pct(late)}</span></span>
            </button>`;
        })
        .join('')}
    </div>
    <div class="legend">
      <span class="legend-item">Line colour = field of service, matching the bars above.</span>
      <span class="legend-item">Left figure = average share across ${FROM}–${FROM + 9};
        right figure = average share across 2020–${lastYear}.</span>
    </div>`;
  wrap.appendChild(p3);
  const sparkGrid = p3.querySelector<HTMLDivElement>('#wf-sparks')!;

  // ---- selection ---------------------------------------------------------
  function paintBars(): void {
    barWrap.querySelectorAll<SVGGElement>('g[data-domain]').forEach((g) => {
      const id = g.getAttribute('data-domain') ?? '';
      g.style.opacity = selected && selected !== id ? '0.25' : '1';
    });
  }

  function describe(): string {
    if (!selected) {
      return `<strong>Nothing selected.</strong> Pick a field — from the dropdown, a bar, a row or a card — to
        highlight it in all three charts and read its numbers here.`;
    }
    const r = rowById.get(selected);
    const bar = barRows.find((b) => b.id === selected);
    if (!r || !bar) return '';
    const sorted = [...r.cells].sort((a, b) => b.z - a.z);
    const hi = sorted[0];
    const lo = sorted[sorted.length - 1];
    const early = windowShare(r.id, FROM, FROM + 9);
    const late = windowShare(r.id, 2020, lastYear);
    const otherCaveat = r.isOther
      ? ` This is the residual “other” bucket, not a field of service — its residuals describe the leftovers
         of the classification, not anything about Australia.`
      : '';
    return `<strong>${escapeHtml(r.label)}</strong> — ${num(bar.n)} citations,
      ${pct(barGrand > 0 ? bar.n / barGrand : NaN)} of all classified.
      Across the four matrix columns (${num(r.total)} of them):
      ${r.cells.map((c) => `${num(c.obs)} ${c.code}`).join(', ')} — ${pct(r.topShare)} at AC or AO.
      Most over-represented at <strong>${escapeHtml(hi.levelLabel)}</strong>: ${num(hi.obs)} observed against
      ${dec(hi.exp, 1)} expected, residual ${signed(hi.z)}.
      Most under-represented at <strong>${escapeHtml(lo.levelLabel)}</strong>: ${num(lo.obs)} against
      ${dec(lo.exp, 1)} expected, residual ${signed(lo.z)}.
      Share of citations moved from ${pct(early)} in the ${FROM}s to ${pct(late)} in the 2020s.${otherCaveat}`;
  }

  function paint(): void {
    paintBars();
    matrixSvgEl.querySelectorAll<SVGGElement>('g[data-domain]').forEach((g) => {
      const id = g.getAttribute('data-domain') ?? '';
      const on = !selected || selected === id;
      g.style.opacity = on ? '1' : '0.28';
      const box = g.querySelector<SVGRectElement>('.wf-rowbg');
      if (box) box.setAttribute('opacity', selected === id ? '1' : '0');
    });
    sparkGrid.querySelectorAll<HTMLButtonElement>('.wf-card').forEach((c) => {
      const id = c.getAttribute('data-domain') ?? '';
      c.setAttribute('aria-pressed', selected === id ? 'true' : 'false');
      c.classList.toggle('is-dim', Boolean(selected) && selected !== id);
    });
    pick.value = selected ?? '';
    clearBtn.disabled = !selected;
    clearBtn.style.opacity = selected ? '1' : '0.5';
    readout.innerHTML = describe();
  }

  function choose(id: string | null, toggle = false): void {
    const next = id && rowById.has(id) ? (toggle && selected === id ? null : id) : null;
    selected = next;
    paint();
    try {
      history.replaceState(null, '', `#/whatfor${next ? `?d=${encodeURIComponent(next)}` : ''}`);
    } catch {
      /* a blocked history write must never break the view */
    }
  }

  wrap.addEventListener('click', (e) => {
    const hit = (e.target as Element | null)?.closest<Element>('[data-domain]');
    if (!hit || !wrap.contains(hit)) return;
    if (hit === pick) return;
    choose(hit.getAttribute('data-domain'), true);
  });

  wrap.addEventListener('keydown', (e) => {
    const ev = e as KeyboardEvent;
    if (ev.key !== 'Enter' && ev.key !== ' ') return;
    const hit = (ev.target as Element | null)?.closest<Element>('rect[data-domain]');
    if (!hit) return;
    ev.preventDefault();
    choose(hit.getAttribute('data-domain'), true);
  });

  pick.addEventListener('change', () => choose(pick.value || null));
  clearBtn.addEventListener('click', () => choose(null));

  drawBars();
  paint();

  let frame = 0;
  const ro = new ResizeObserver(() => {
    if (frame) cancelAnimationFrame(frame);
    frame = requestAnimationFrame(() => {
      frame = 0;
      drawBars();
      paintBars();
    });
  });
  ro.observe(barWrap);

  return () => {
    if (frame) cancelAnimationFrame(frame);
    ro.disconnect();
  };
};
